import { CountryCode } from "../types/common";
import { type AppEnvironment, isAppEnvironment } from "./environment";

/**
 * Every non-secret per-environment x per-country value, in one place.
 * Pure data: MUST NOT reference `import.meta` or `process`, so Next.js and
 * Node (vite config) can import it. Everything here ships in public bundles.
 */
export interface ICountryAppUrls {
  customer: string;
  lender: string;
  partner: string;
  admin: string;
  /** Marketing site (Next). */
  website: string;
}

/** Environment-derived switches. Replaces ad-hoc `environment === "production"` checks. */
export interface IEnvironmentFlags {
  /** Sentry reports errors. staging + production only (not `test`). */
  sentryEnabled: boolean;
  /** Websites may be indexed by search engines (otherwise `noindex, nofollow`). */
  searchIndexing: boolean;
  /** Loan widget redirects through the Adtraction affiliate network. */
  affiliateRedirects: boolean;
  /** BankID polling runs at 200 ms instead of the production interval (test automation). */
  fastBankIdPolling: boolean;
  /** Loan-widget advisory bookings go to the real advisor calendars (production only). */
  realAdvisorCalendars: boolean;
}

export interface IEnvironmentConfig {
  /** core-service base URL, no trailing slash. */
  apiRootUrl: string;
  /** Lender integration API docs. */
  lenderApiDocsUrl: string;
  apps: Record<CountryCode, ICountryAppUrls>;
  flags: IEnvironmentFlags;
}

const PRODUCTION_API_ROOT_URL = "https://customer-api.krea.se";

const NON_PRODUCTION_FLAGS: IEnvironmentFlags = {
  sentryEnabled: false,
  searchIndexing: false,
  affiliateRedirects: false,
  fastBankIdPolling: false,
  realAdvisorCalendars: false,
};

const LOCAL_URLS: IEnvironmentConfig = {
  apiRootUrl: "http://localhost:8000",
  lenderApiDocsUrl: "http://localhost:8000/lender-integration/api-docs",
  apps: {
    [CountryCode.SE]: {
      customer: "http://localhost:3003",
      lender: "http://localhost:3000",
      partner: "http://localhost:3004",
      admin: "http://localhost:3005",
      website: "http://localhost:4000",
    },
    [CountryCode.FI]: {
      customer: "http://localhost:3003",
      lender: "http://localhost:3000",
      partner: "http://localhost:3004",
      admin: "http://localhost:3005",
      website: "http://localhost:4001",
    },
  },
  flags: NON_PRODUCTION_FLAGS,
};

export const CONFIG_CATALOG: Record<AppEnvironment, IEnvironmentConfig> = {
  development: LOCAL_URLS,
  "local-docker": LOCAL_URLS,
  test: {
    apiRootUrl: "https://test.krea.se",
    lenderApiDocsUrl: "https://test-api.krea.se/api-docs",
    apps: {
      [CountryCode.SE]: {
        customer: "https://test-app.krea.se",
        lender: "https://test-lender.krea.se",
        partner: "https://test-partner.krea.se",
        admin: "https://test-admin.krea.se",
        website: "https://test-next.krea.se",
      },
      // The old *.kredita.fi test hosts do not resolve in DNS, so FI test
      // points at the working *.krea.se test hosts (KREA-4314). The website
      // keeps test-next.kredita.fi: it is the CMS tenant key of the FI site.
      [CountryCode.FI]: {
        customer: "https://test-app.krea.se",
        lender: "https://test-lender.krea.se",
        partner: "https://test-partner.krea.se",
        admin: "https://test-admin.krea.se",
        website: "https://test-next.kredita.fi",
      },
    },
    // Test automation: fast BankID polling; Sentry stays off.
    flags: { ...NON_PRODUCTION_FLAGS, fastBankIdPolling: true },
  },
  staging: {
    apiRootUrl: "https://staging.krea.se",
    lenderApiDocsUrl: "https://staging-api.krea.se/api-docs",
    apps: {
      [CountryCode.SE]: {
        customer: "https://staging-app.krea.se",
        lender: "https://staging-lender.krea.se",
        partner: "https://staging-partner.krea.se",
        admin: "https://staging-admin.krea.se",
        website: "https://staging-next.krea.se",
      },
      [CountryCode.FI]: {
        customer: "https://staging-app.kredita.fi",
        lender: "https://staging-lender.krea.se",
        partner: "https://staging-partner.kredita.fi",
        admin: "https://staging-admin.krea.se",
        website: "https://staging-next.kredita.fi",
      },
    },
    flags: { ...NON_PRODUCTION_FLAGS, sentryEnabled: true },
  },
  production: {
    apiRootUrl: PRODUCTION_API_ROOT_URL,
    lenderApiDocsUrl: "https://api.krea.se/api-docs",
    apps: {
      [CountryCode.SE]: {
        customer: "https://app.krea.se",
        lender: "https://lender.krea.se",
        partner: "https://partner.krea.se",
        admin: "https://admin.krea.se",
        website: "https://krea.se",
      },
      [CountryCode.FI]: {
        customer: "https://app.kredita.fi",
        lender: "https://lender.krea.se",
        partner: "https://partner.kredita.fi",
        admin: "https://admin.krea.se",
        website: "https://kredita.fi",
      },
    },
    flags: {
      sentryEnabled: true,
      searchIndexing: true,
      affiliateRedirects: true,
      fastBankIdPolling: false,
      realAdvisorCalendars: true,
    },
  },
};

/**
 * Flags for an environment given as a plain string (website `settings.environment`,
 * widget props). Unknown or missing environments have every flag off.
 */
export const getEnvironmentFlags = (
  environment: string | undefined,
): IEnvironmentFlags =>
  isAppEnvironment(environment)
    ? CONFIG_CATALOG[environment].flags
    : NON_PRODUCTION_FLAGS;
